import React, { createContext, FC, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import {
	AuthenticatedTemplate,
	UnauthenticatedTemplate
} from '@azure/msal-react'
import '@components/layouts/Fonts.css'
import '@components/layouts/global.scss'
import 'reflect-metadata'

import LayoutWithProvider from '@components/layouts/LayoutWithProvider'
import LayoutWithSidebarWithProvider from '@components/layouts/LayoutWithSidebarWithProvider'
import LayoutRequestWithProvider from '@components/layouts/LayoutRequestWithProvider'
import LayoutWithSideContentWithProvider from '@components/layouts/LayoutWithSideContentWithProvider'
import RedirectComponent from '@components/auth/RedirectComponent'
import config from '@utils/config'
import store from '@services/store'
import {
	pathToPageName,
	sortRequestThemes,
	transformStrapiData
} from '@utils/methods'
import {
	isAuthPage,
	isAuthRedirectPage,
	isAuthUserInfoPage,
	isCreateNewRequest,
	isCreateRequestPage,
	isPaymentPage,
	isProfileCompletionRoute
} from '@utils/layoutRoutes'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { PageDataType, TempAllTextType } from '../../typings/shared'
import { User } from '@services/models'
import { LanguageEnum } from '@services/constants'
import { Request } from '@services/models'
import {
	ElectorialDistrict,
	TeamMember
} from '@services/models/_miscellaneous.model'
import { PageProps } from 'gatsby'
import { Article } from '@services/models/articles.model'
import { Helmet } from 'react-helmet'
import SEO from '@components/configs/SEO'
import * as layoutUtils from './__layout.utils'
import moment from 'moment'
import {
	getAuthState,
	initAuthFromCache,
	subscribeAuthState
} from '@utils/authResolver'
import { updateQualtricsSiteLanguage } from '@utils/qualtrics'

export type AppContextProps = {
	pathname: string
	urlParams: URLSearchParams
	language: LanguageEnum | undefined
	setLanguage: (language: LanguageEnum) => void
	setAuthUser: (user: User | null | undefined) => void
	pageData: PageDataType
	electedTeamMembers?: TeamMember[]
	setElectedTeamMembers?: (members: TeamMember[]) => void
	electorialDistricts?: ElectorialDistrict[]
	setElectorialDistricts?: (districts: ElectorialDistrict[]) => void
	authUser?: User | null
	userRequest?: Request
	setUserRequest?: (request: Request) => void
	currentArticle?: Article
	setCurrentArticle: (article: Article | undefined) => void
	tempAllText?: TempAllTextType
}

export const AppStateContext = createContext<AppContextProps>({
	pathname: '',
	urlParams: new URLSearchParams(),
	language: LanguageEnum.FR,
	setLanguage: () => null,
	setAuthUser: () => null,
	setUserRequest: () => null,
	setCurrentArticle: () => null,
	setElectedTeamMembers: () => null,
	setElectorialDistricts: () => null,
	pageData: {} as PageDataType,
	electedTeamMembers: [] as TeamMember[],
	electorialDistricts: [] as ElectorialDistrict[],
	authUser: {} as User,
	tempAllText: {} as TempAllTextType
})

type UserLastLoginData = {
	email: string
	date: string
}

const DynamicLayout: FC<PageProps> = ({ location, children }) => {
	const [language, setLocalLanguage] = useState<LanguageEnum>()
	const [authUser, setLocalAuthUser] = useState<User | undefined | null>()
	const [userRequest, setUserRequest] = useState<Request>()
	const [currentArticle, setCurrentArticle] = useState<Article>()
	const [electedTeamMembers, setElectedTeamMembers] = useState<TeamMember[]>([])
	const [electorialDistricts, setElectorialDistricts] = useState<
		ElectorialDistrict[]
	>([])

	const getLanguage = (): LanguageEnum => {
		const lang: LanguageEnum = (localStorage.getItem(
			config.localStorage.languageKey
		) || LanguageEnum.FR) as LanguageEnum

		return lang
	}

	const setAuthUser = (user: User | null | undefined) => {
		setLocalAuthUser(user || null)
	}

	const setLanguage = (language: LanguageEnum | null) => {
		const selectedLanguage = language || LanguageEnum.FR
		if (language) {
			localStorage.setItem(config.localStorage.languageKey, language)
			setLocalLanguage(language)
		} else {
			localStorage.removeItem(config.localStorage.languageKey)
			setLocalLanguage(LanguageEnum.FR)
		}

		void updateQualtricsSiteLanguage(selectedLanguage)
	}

	const isArray = (value: string): boolean => {
		try {
			const parsedValue = JSON.parse(value)
			return Array.isArray(parsedValue)
		} catch (error) {
			return false
		}
	}

	const getLastLoginDate = () => {
		const userData = localStorage.getItem(config.localStorage.lastLoginDateKey)

		if (!userData || !isArray(userData)) return null

		const userInfo: UserLastLoginData[] = JSON.parse(userData)
		const userExist = userInfo?.find(
			(user) => user.email == authUser?.profile?.email
		)
		if (userExist) return userExist?.date
		return null
	}

	const saveUser = (userInfo?: UserLastLoginData[]) => {
		const lastLoginDateKey = config.localStorage.lastLoginDateKey

		const newUserLastLoginData: UserLastLoginData = {
			email: authUser?.profile?.email || '',
			date: moment().format('YYYY-MM-DD HH:mm:ss')
		}

		const userLastLoginDataArray: UserLastLoginData[] = userInfo
			? [...userInfo, newUserLastLoginData]
			: [newUserLastLoginData]

		localStorage.setItem(
			lastLoginDateKey,
			JSON.stringify(userLastLoginDataArray)
		)
	}

	const addIfNotExist = () => {
		const userData = localStorage.getItem(config.localStorage.lastLoginDateKey)
		// first time login in
		if (!userData) {
			saveUser()
		} else {
			try {
				// verify if the current user has a last login date an add if not
				const userInfo: UserLastLoginData[] = JSON.parse(userData)
				const userExist = userInfo?.find(
					(user) => user.email == authUser?.profile?.email
				)
				if (!userExist) {
					saveUser(userInfo)
				}
			} catch (err) {
				localStorage.removeItem(config.localStorage.lastLoginDateKey)
				saveUser()
			}
		}
	}

	const setLastLoginDate = (date: string | null) => {
		const accountExists = localStorage.getItem('tnsApp')

		if (date === null && accountExists !== null) {
			addIfNotExist()
			localStorage.setItem('updateProfilePopup', 'true')
		} else {
			addIfNotExist()
		}
	}

	const pathname: string = location?.pathname

	const normalizePath = (path: string): string => {
		if (!path) {
			return '/'
		}

		const normalizedPath = path.replace(/\/+$/, '')
		return normalizedPath || '/'
	}

	const isProtectedAuthRoute = (path: string): boolean => {
		const normalizedPath = normalizePath(path)
		if (normalizedPath === '/auth' || normalizedPath === '/auth-redirect') {
			return false
		}

		if (normalizedPath === '/auth/requests/create') {
			return false
		}

		return normalizedPath.startsWith('/auth/')
	}

	useEffect(() => {
		const shouldSkipResolverInit =
			isProfileCompletionRoute(pathname) || isAuthRedirectPage(pathname)

		console.log(
			performance.now(),
			`[DynamicLayout] useEffect triggered: pathname=${pathname}, shouldSkipResolverInit=${shouldSkipResolverInit}`
		)
		console.log(
			performance.now(),
			`[DynamicLayout] useEffect isAuthRedirectPage(pathname)=${isAuthRedirectPage(
				pathname
			)}, isProfileCompletionRoute(pathname)=${isProfileCompletionRoute(
				pathname
			)}`
		)

		if (!shouldSkipResolverInit) {
			void initAuthFromCache()
		}

		const unsubscribe = subscribeAuthState(({ status, user }) => {
			console.log(
				performance.now(),
				`[DynamicLayout] useEffect triggered: status=${status}`
			)

			if (status === 'resolved') {
				setAuthUser(user)
				return
			}

			if (status === 'unauthenticated') {
				setAuthUser(null)
				if (isProtectedAuthRoute(pathname)) {
					const authRedirectKey = config.localStorage.authRedirect
					const targetPath = pathname || config.homepage.baseURL
					if (targetPath !== '/auth' && targetPath !== '/auth/') {
						localStorage.setItem(authRedirectKey, targetPath)
					}
					void navigate('/auth', { replace: true })
				}
				return
			}

			if (status === 'profile_incomplete') {
				setAuthUser(null)
				if (!isProfileCompletionRoute(pathname)) {
					void navigate('/auth', { replace: true })
				}
				return
			}

			if (status === 'error') {
				setAuthUser(null)
			}
		})

		return () => {
			unsubscribe()
		}
	}, [pathname])

	// REPERE sync: triggered whenever the authenticated user changes (login, account switch,
	// or after auth-redirect.tsx sets the new user following a B2C redirect).
	// Moved out of the one-time fetchUser effect so it fires correctly even when
	// DynamicLayout skips fetchUser on the auth-redirect page.
	useEffect(() => {
		if (!authUser?.profile?.id) {
			return
		}

		const userId = authUser.profile.id
		const syncIntervalMinutes = Number(
			process.env.REPERE_SYNC_INTERVAL_MINUTES || 30
		)

		const timer = setTimeout(() => {
			const lastSynchRepere = localStorage.getItem('lastSynchRepere')

			const doSync = () => {
				User.forceSyncRequests(userId)
					.then(() => {
						localStorage.setItem('lastSynchRepere', new Date().toISOString())
					})
					.catch(console.error)
			}

			if (!lastSynchRepere) {
				doSync()
			} else {
				const diffInMinutes =
					(Date.now() - new Date(lastSynchRepere).getTime()) / (1000 * 60)

				if (diffInMinutes >= syncIntervalMinutes) {
					doSync()
				}
			}
		}, 1000)

		return () => clearTimeout(timer)
	}, [authUser?.profile?.id])

	useEffect(() => {
		const fetchElectedTeamMembers = async () => {
			setElectedTeamMembers(await TeamMember.fetchAllByAsync())
			TeamMember.preloadElected()
		}

		const fetchElectorialDistricts = async () => {
			setElectorialDistricts(
				await ElectorialDistrict.getElectorialDistrictsAsync()
			)
		}

		if (authUser) {
			fetchElectedTeamMembers()
			fetchElectorialDistricts()
		}
	}, [authUser])

	useEffect(() => {
		if (authUser) setLastLoginDate(getLastLoginDate())
		setLanguage(getLanguage())
	}, [authUser])

	const urlParams: URLSearchParams = new URLSearchParams(location?.search)

	const strapiData = useStaticQuery(graphql`
		query AllStrapiContentQuery {
			allStrapiPtcPage {
				edges {
					node {
						id
						title
						uri
						description
						ptc_assets {
							key
							value {
								data {
									value
								}
							}
						}
						locale
						name
					}
				}
			}
			allStrapiPtcAsset {
				edges {
					node {
						key
						value {
							data {
								value
							}
						}
						locale
					}
				}
			}
			allStrapiPtcRequest {
				edges {
					node {
						description {
							data {
								description
							}
						}
						locale
						name
						request_id
						template_type
						title
						ptc_collections {
							group
							id
							name
							title
							order
							description {
								data {
									description
								}
							}
						}
						ptc_tags {
							value
						}
					}
				}
			}
			allStrapiPtcForm {
				edges {
					node {
						cacher
						description {
							data {
								description
							}
						}
						form {
							url
							ext
						}
						locale
						name
						order
						title
						request_id
						ptc_collections {
							group
							id
							name
							title
							order
							description {
								data {
									description
								}
							}
						}
					}
				}
			}
		}
	`)

	const pageName = pathToPageName(pathname) || 'home'

	const tempAllText = transformStrapiData(strapiData)

	const allText =
		tempAllText[language || LanguageEnum.FR] ||
		({ pages: {}, collections: {} } as PageDataType)

	const pageData = allText[pageName] || {}

	pageData.pages = allText?.pages
	pageData.assets = { ...allText?.assets, ...allText[pageName]?.assets }
	pageData.allAssets = {}
	Object.keys(tempAllText).forEach(
		(key) => (pageData.allAssets[key] = tempAllText[key].assets)
	)

	pageData.collections = allText?.collections

	pageData.collections.requestThemes = sortRequestThemes(
		allText?.collections?.requestThemes as []
	)
	pageData.frenchRequestThemes =
		tempAllText[LanguageEnum.FR]?.collections?.requestThemes || []

	pageData.requests = allText.requests

	pageData.formRequests = allText.formRequests

	// this is a workaround as Gatsby is not mature enougth to support our use case:
	// multiple layouts providers with shared state and azure auth component wrapper :(
	if (isAuthPage(pathname) && !isCreateRequestPage(pathname)) {
		return (
			<Provider store={store}>
				<AppStateContext.Provider
					value={{
						pathname,
						urlParams,
						language,
						setLanguage,
						pageData,
						setAuthUser,
						authUser,
						userRequest,
						setUserRequest,
						currentArticle,
						setCurrentArticle,
						electedTeamMembers,
						electorialDistricts,
						tempAllText
					}}
				>
					<Helmet htmlAttributes={{ lang: language || LanguageEnum.FR }} />
					<SEO />
					<LayoutWithProvider>
						<AuthenticatedTemplate>
							{(isCreateNewRequest(pathname) ||
								isAuthRedirectPage(pathname) ||
								isPaymentPage(pathname)) && (
								<LayoutRequestWithProvider
									wrapperClass={
										isPaymentPage(pathname)
											? layoutUtils.classes.paymentContainer
											: ''
									}
								>
									{children}
								</LayoutRequestWithProvider>
							)}
							{isAuthUserInfoPage(pathname) && !isPaymentPage(pathname) && (
								<LayoutWithSideContentWithProvider>
									{children}
								</LayoutWithSideContentWithProvider>
							)}
							{!isCreateNewRequest(pathname) &&
								!isAuthRedirectPage(pathname) &&
								!isAuthUserInfoPage(pathname) &&
								!isPaymentPage(pathname) && (
									<LayoutWithSidebarWithProvider>
										{children}
									</LayoutWithSidebarWithProvider>
								)}
						</AuthenticatedTemplate>
						<UnauthenticatedTemplate>
							<RedirectComponent />
						</UnauthenticatedTemplate>
					</LayoutWithProvider>
				</AppStateContext.Provider>
			</Provider>
		)
	}

	return (
		<Provider store={store}>
			<AppStateContext.Provider
				value={{
					pathname,
					urlParams,
					language,
					setLanguage,
					pageData,
					setAuthUser,
					authUser,
					userRequest,
					setUserRequest,
					currentArticle,
					setCurrentArticle,
					electedTeamMembers,
					electorialDistricts,
					tempAllText
				}}
			>
				<Helmet htmlAttributes={{ lang: language || LanguageEnum.FR }} />
				<SEO />

				<LayoutWithProvider>
					<LayoutWithSidebarWithProvider>
						{children}
					</LayoutWithSidebarWithProvider>
				</LayoutWithProvider>
			</AppStateContext.Provider>
		</Provider>
	)
}

export default DynamicLayout
